const benefits = [
  {
    title: 'Fully Licensed & Insured',
    desc: 'All certifications current and maintained. Comprehensive insurance coverage protecting your project, your property, and your team from day one.',
    icon: 'ri-shield-star-line',
  },
  {
    title: 'Strict EPA / OSHA Regulatory Compliance',
    desc: 'Every project executed in full compliance with EPA, OSHA, and Colorado state environmental regulations — with documentation to prove it.',
    icon: 'ri-verified-badge-line',
  },
  {
    title: 'High-Risk Environment Expertise',
    desc: 'Deep experience handling the most complex industrial and commercial hazardous material scenarios, including government and high-liability projects.',
    icon: 'ri-building-4-line',
  },
  {
    title: 'Transparent Documentation & Reporting',
    desc: 'Complete written reporting, air quality clearance certificates, and chain-of-custody documentation delivered on every project — no exceptions.',
    icon: 'ri-file-text-line',
  },
  {
    title: 'Minimal Disruption to Operations',
    desc: 'Precise scheduling, contained work zones, and efficient execution designed to keep your business running throughout the remediation process.',
    icon: 'ri-time-line',
  },
  {
    title: '24/7 Emergency Response',
    desc: 'Environmental emergencies don\'t wait for business hours. Our rapid-response teams are available around the clock for critical situations.',
    icon: 'ri-alarm-warning-line',
  },
];

interface WhyChooseUsSectionProps {
  headline?: string;
  extraBenefits?: string[];
}

export default function WhyChooseUsSection({
  headline = 'Precision, Compliance, and Experience — On Every Project',
}: WhyChooseUsSectionProps) {
  return (
    <section className="bg-white py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16 items-end">
          <div className="lg:col-span-6">
            <p className="text-ecos-green text-xs uppercase tracking-widest font-semibold mb-4">Why ECOS</p>
            <h2 className="font-serif text-ecos-charcoal text-4xl lg:text-5xl font-semibold leading-tight">
              {headline}
            </h2>
          </div>
          <div className="lg:col-span-6">
            <div className="border-l-2 border-ecos-green pl-6">
              <p className="text-ecos-text text-base leading-relaxed">
                When the work involves hazardous materials, regulatory risk, or public safety — there is no room for error. ECOS Environmental delivers certified solutions that hold up under scrutiny, documentation that stands in regulatory review, and results that protect your organization long-term.
              </p>
            </div>
          </div>
        </div>

        {/* Benefits grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit) => (
            <div
              key={benefit.title}
              className="group border border-ecos-border rounded-sm p-7 hover:border-ecos-green hover:bg-ecos-green-pale transition-all duration-300 cursor-default"
            >
              <div className="w-11 h-11 flex items-center justify-center bg-ecos-slate rounded-sm mb-5 group-hover:bg-white transition-colors">
                <i className={`${benefit.icon} text-ecos-green text-xl`}></i>
              </div>
              <h3 className="font-serif text-ecos-charcoal text-lg font-semibold mb-3 leading-snug group-hover:text-ecos-green-deep transition-colors">
                {benefit.title}
              </h3>
              <p className="text-ecos-text text-sm leading-relaxed">{benefit.desc}</p>
            </div>
          ))}
        </div>

        {/* Stats strip */}
        <div className="mt-16 bg-ecos-charcoal rounded-sm px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { value: '25+', label: 'Years in Operation' },
            { value: '3,000+', label: 'Projects Completed' },
            { value: '100%', label: 'Compliance Rate' },
            { value: '4.9★', label: 'Average Client Rating' },
          ].map((stat) => (
            <div key={stat.value} className="text-center">
              <p className="font-serif text-ecos-gold text-3xl lg:text-4xl font-semibold">{stat.value}</p>
              <p className="text-white/50 text-xs mt-2 tracking-wide">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
